import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaSave, FaTrash, FaDrawPolygon } from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const SignatureBoxEditor = ({ isOpen, onClose, documentUrl, existingBoxes = [], onSave, isSaving }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [boxes, setBoxes] = useState(existingBoxes);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [scale, setScale] = useState(1);
  const [, setPdfPageDimensions] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(true);

  // Load PDF document
  useEffect(() => {
    if (!isOpen || !documentUrl) return;
    setLoading(true);
    const loadPdf = async () => {
      try {
        const doc = await pdfjsLib.getDocument(documentUrl).promise;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setCurrentPage(1);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setLoading(false);
      }
    };
    loadPdf();
  }, [isOpen, documentUrl]);

  // Render current page
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;
    try {
      const page = await pdfDoc.getPage(currentPage);
      const container = containerRef.current;
      const containerWidth = container ? container.clientWidth - 48 : 800;
      const viewport = page.getViewport({ scale: 1 });
      
      // Calculate display scale to fit container
      const displayScale = containerWidth / viewport.width;
      setScale(displayScale);
      
      // Use higher render scale for quality (2x for retina, min 2.0 for crisp rendering)
      const devicePixelRatio = window.devicePixelRatio || 1;
      const renderScale = displayScale * Math.max(devicePixelRatio, 2.0);
      
      const renderViewport = page.getViewport({ scale: renderScale });
      const canvas = canvasRef.current;
      canvas.width = renderViewport.width;
      canvas.height = renderViewport.height;
      
      // Set CSS size to display scale for proper sizing
      canvas.style.width = `${viewport.width * displayScale}px`;
      canvas.style.height = `${viewport.height * displayScale}px`;
      
      setPdfPageDimensions({ width: viewport.width * displayScale, height: viewport.height * displayScale });

      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport: renderViewport }).promise;
    } catch (err) {
      console.error('Error rendering page:', err);
    }
  }, [pdfDoc, currentPage]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  // Get mouse position relative to canvas
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleMouseDown = (e) => {
    const pos = getMousePos(e);
    setIsDrawing(true);
    setDrawStart(pos);
    setDrawCurrent(pos);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    setDrawCurrent(getMousePos(e));
  };

  const handleMouseUp = () => {
    if (!isDrawing || !drawStart || !drawCurrent) {
      setIsDrawing(false);
      return;
    }

    const x = Math.min(drawStart.x, drawCurrent.x);
    const y = Math.min(drawStart.y, drawCurrent.y);
    const width = Math.abs(drawCurrent.x - drawStart.x);
    const height = Math.abs(drawCurrent.y - drawStart.y);

    // Minimum box size of 40x20
    if (width >= 40 && height >= 20) {
      const newBox = {
        x: x / scale,
        y: y / scale,
        width: width / scale,
        height: height / scale,
        page: currentPage - 1 // 0-indexed
      };
      setBoxes(prev => [...prev, newBox]);
    }

    setIsDrawing(false);
    setDrawStart(null);
    setDrawCurrent(null);
  };

  const removeBox = (index) => {
    setBoxes(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (onSave) onSave(boxes);
  };

  const currentPageBoxes = boxes.filter(b => b.page === currentPage - 1);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <FaDrawPolygon /> Signature Box Editor
            </h2>
            <p className="text-purple-200 text-sm mt-1">
              Draw rectangles where you want the user to sign
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors">
            <FaTimes size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition"
            >
              <FaChevronLeft />
            </button>
            <span className="text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition"
            >
              <FaChevronRight />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {boxes.length} signature box{boxes.length !== 1 ? 'es' : ''} total ({currentPageBoxes.length} on this page)
            </span>
            <button
              onClick={handleSave}
              disabled={boxes.length === 0 || isSaving}
              className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
            >
              <FaSave />
              {isSaving ? 'Saving...' : 'Save Boxes & Set Client Pending'}
            </button>
          </div>
        </div>

        {/* Canvas Area */}
        <div ref={containerRef} className="flex-1 overflow-auto p-6 bg-gray-100 flex justify-center">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-gray-500">Loading PDF...</p>
              </div>
            </div>
          ) : (
            <div className="relative inline-block shadow-xl rounded-lg bg-white">
              <canvas
                ref={canvasRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={() => { if (isDrawing) { setIsDrawing(false); setDrawStart(null); setDrawCurrent(null); } }}
                className="block cursor-crosshair"
                style={{ display: 'block' }}
              />
              {/* Existing boxes overlay */}
              {currentPageBoxes.map((box, idx) => {
                const globalIdx = boxes.findIndex(b => b === box);
                return (
                  <div
                    key={idx}
                    className="absolute border-2 border-dashed border-red-500 bg-red-500 bg-opacity-10 flex items-start justify-end group"
                    style={{
                      left: box.x * scale,
                      top: box.y * scale,
                      width: box.width * scale,
                      height: box.height * scale,
                    }}
                  >
                    <button
                      onClick={() => removeBox(globalIdx)}
                      className="bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs -mt-2 -mr-2 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                    >
                      <FaTimes size={10} />
                    </button>
                    <span className="absolute bottom-0 left-1 text-[10px] text-red-600 font-bold bg-white bg-opacity-80 px-1 rounded">
                      Sign {globalIdx + 1}
                    </span>
                  </div>
                );
              })}
              {/* Drawing preview */}
              {isDrawing && drawStart && drawCurrent && (
                <div
                  className="absolute border-2 border-dashed border-blue-500 bg-blue-500 bg-opacity-15 pointer-events-none"
                  style={{
                    left: Math.min(drawStart.x, drawCurrent.x),
                    top: Math.min(drawStart.y, drawCurrent.y),
                    width: Math.abs(drawCurrent.x - drawStart.x),
                    height: Math.abs(drawCurrent.y - drawStart.y),
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Box List Footer */}
        {boxes.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 max-h-32 overflow-y-auto">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">All Signature Boxes</p>
            <div className="flex flex-wrap gap-2">
              {boxes.map((box, idx) => (
                <span key={idx} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${box.page === currentPage - 1 ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'}`}>
                  Sign {idx + 1} (Page {box.page + 1})
                  <button onClick={() => removeBox(idx)} className="hover:text-red-900">
                    <FaTrash size={10} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignatureBoxEditor;
